import React from "react";
import { useNavigate } from "react-router-dom";
import { User, LogIn, UserPlus, LayoutDashboard, ShieldCheck, LogOut, Store } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Profile = () => {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const roleLabel = { buyer: t("auth.roleBuyer"), seller: t("auth.roleSeller"), admin: t("nav.admin") };

  if (!user) {
    return (
      <div className="max-w-sm mx-auto px-4 py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-[#DCE7E2] flex items-center justify-center mx-auto mb-4">
          <User className="w-8 h-8 text-[#123A2C]" />
        </div>
        <h1 className="text-xl font-bold text-[#14251F] mb-2">{t("profile.title")}</h1>
        <p className="text-sm text-[#6B7280] mb-6">{t("auth.welcome")}</p>
        <div className="space-y-3">
          <button data-testid="profile-login" onClick={() => navigate("/login")} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#123A2C] text-white font-semibold">
            <LogIn className="w-5 h-5" /> {t("auth.loginBtn")}
          </button>
          <button data-testid="profile-signup" onClick={() => navigate("/signup")} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-white border border-[#E5E7EB] text-[#123A2C] font-semibold">
            <UserPlus className="w-5 h-5" /> {t("auth.signupBtn")}
          </button>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    toast.success(t("toast.logoutSuccess"));
    navigate("/");
  };

  return (
    <div className="max-w-md mx-auto px-4 py-8">
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-[#DCE7E2] flex items-center justify-center">
          <User className="w-7 h-7 text-[#123A2C]" />
        </div>
        <div>
          <div className="font-extrabold text-[#14251F] text-lg" data-testid="profile-name">{user.name}</div>
          <div className="text-sm text-[#6B7280]">{user.phone}</div>
          <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-[#DCE7E2] text-[#123A2C]">{roleLabel[user.role]}</span>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {user.role === "seller" && (
          <>
            <button data-testid="profile-dashboard" onClick={() => navigate("/dashboard")} className="w-full flex items-center gap-3 p-4 rounded-xl bg-white border border-[#E5E7EB] text-[#14251F] font-medium">
              <LayoutDashboard className="w-5 h-5 text-[#123A2C]" /> {t("dashboard.title")}
            </button>
            <button data-testid="profile-add" onClick={() => navigate("/dashboard/add")} className="w-full flex items-center gap-3 p-4 rounded-xl bg-white border border-[#E5E7EB] text-[#14251F] font-medium">
              <Store className="w-5 h-5 text-[#123A2C]" /> {t("nav.addProduct")}
            </button>
          </>
        )}
        {user.role === "admin" && (
          <button data-testid="profile-admin" onClick={() => navigate("/admin")} className="w-full flex items-center gap-3 p-4 rounded-xl bg-white border border-[#E5E7EB] text-[#14251F] font-medium">
            <ShieldCheck className="w-5 h-5 text-[#123A2C]" /> {t("admin.title")}
          </button>
        )}
        <button data-testid="profile-logout" onClick={handleLogout} className="w-full flex items-center gap-3 p-4 rounded-xl bg-white border border-[#E5E7EB] text-[#DC2626] font-medium">
          <LogOut className="w-5 h-5" /> {t("nav.logout")}
        </button>
      </div>
    </div>
  );
};

export default Profile;
